import type {
  AttachmentResponse,
  DeviceRequest,
  InspectionDeviceEntryResponse,
  InspectionDeviceRecordRequest,
  InspectionResponse,
  InspectionTransitionRequest,
} from 'src/api/ProactApiClient';
import {
  DependsOnFailedItemError,
  DeterministicExecutorError,
} from 'src/offline/executorRegistry';
import type {
  OutboxExecutor,
  OutboxExecutorContext,
} from 'src/offline/executorRegistry';
import {presignAndUploadBlob} from 'src/offline/fileUpload';
import {findSessionFile} from 'src/offline/files';
import {findParentOutboxItem} from 'src/offline/outbox';
import {normalizeApiError} from 'src/redux/normalizeApiError';
import {TRANSITION_TARGET} from './appliers';

// The API client may only be imported by src/redux and src/offline, so the
// replay calls are injected; see src/redux/queries/inspectionOfflineRegistration.ts.
export interface InspectionOutboxApi {
  recordDevice: (
    id: number,
    deviceId: number,
    request: InspectionDeviceRecordRequest
  ) => Promise<InspectionDeviceEntryResponse>;
  addCustomDevice: (
    id: number,
    request: DeviceRequest
  ) => Promise<InspectionDeviceEntryResponse>;
  addDeviceAttachment: (
    id: number,
    deviceId: number,
    request: {
      name: string;
      contentUrl: string;
      key: string;
      clientSubmissionId: string;
    }
  ) => Promise<AttachmentResponse>;
  transition: (
    id: number,
    request: InspectionTransitionRequest
  ) => Promise<InspectionResponse>;
  get: (id: number) => Promise<InspectionResponse>;
}

export const INSPECTION_OUTBOX_KINDS = {
  recordDevice: 'inspection.recordDevice',
  addCustomDevice: 'inspection.addCustomDevice',
  addAttachment: 'inspection.addAttachment',
  transition: 'inspection.transition',
} as const;

// Temp custom-device ids are negative; resolve them to the real id mapped by
// the addCustomDevice executor, else the source create hasn't (yet) succeeded.
const resolveDeviceId = (
  deviceId: number,
  ctx: OutboxExecutorContext
): number => {
  if (deviceId >= 0) return deviceId;
  const real = ctx.resolveId(String(deviceId));
  if (real === undefined) {
    // The addCustomDevice sibling owning this temp id lets the sync manager tell
    // a transient parent failure from a permanent one.
    throw new DependsOnFailedItemError(
      String(deviceId),
      findParentOutboxItem(
        ctx.sessionId,
        INSPECTION_OUTBOX_KINDS.addCustomDevice,
        item =>
          (item.payload as {tempDeviceId?: number}).tempDeviceId === deviceId
      )
    );
  }
  return Number(real);
};

export const createRecordDeviceExecutor =
  (api: InspectionOutboxApi): OutboxExecutor =>
  async (payload, _csid, ctx) => {
    const {inspectionId, deviceId, request} = payload as {
      inspectionId: number;
      deviceId: number;
      request: InspectionDeviceRecordRequest;
    };
    const entry = await api.recordDevice(
      inspectionId,
      resolveDeviceId(deviceId, ctx),
      request
    );
    return {summary: `Recorded ${entry.deviceName}`};
  };

export const createAddCustomDeviceExecutor =
  (api: InspectionOutboxApi): OutboxExecutor =>
  async (payload, clientSubmissionId, ctx) => {
    const {inspectionId, tempDeviceId, request} = payload as {
      inspectionId: number;
      tempDeviceId: number;
      request: DeviceRequest;
    };
    const created = await api.addCustomDevice(inspectionId, {
      ...request,
      clientSubmissionId,
    });
    await ctx.recordIdMapping(String(tempDeviceId), created.deviceId);
    return {summary: `Added ${created.deviceName}`};
  };

export const createAddAttachmentExecutor =
  (api: InspectionOutboxApi): OutboxExecutor =>
  async (payload, clientSubmissionId, ctx) => {
    const {inspectionId, deviceId, name} = payload as {
      inspectionId: number;
      deviceId: number;
      name: string;
    };
    const realDeviceId = resolveDeviceId(deviceId, ctx);

    const file = await findSessionFile(ctx.sessionId, clientSubmissionId);
    if (!file) {
      throw new DeterministicExecutorError(
        `No captured photo found for submission ${clientSubmissionId}.`
      );
    }

    const {accessUrl, key} = await presignAndUploadBlob({
      filename: file.name,
      presignContentType: file.contentType,
      blob: file.blob,
      uploadContentType: file.contentType,
      heartbeat: ctx.heartbeat,
    });

    await api.addDeviceAttachment(inspectionId, realDeviceId, {
      name,
      contentUrl: accessUrl,
      key,
      clientSubmissionId,
    });
    return {summary: `Uploaded ${name}`};
  };

// Start/Complete replayed after a crash may 4xx because state already advanced;
// treat "already in the target state" as a benign success.
export const createTransitionExecutor =
  (api: InspectionOutboxApi): OutboxExecutor =>
  async payload => {
    const {id, request} = payload as {
      id: number;
      request: InspectionTransitionRequest;
    };
    try {
      await api.transition(id, request);
      return {summary: `Inspection ${request.transition}`};
    } catch (error) {
      const {status} = normalizeApiError(error);
      if (status && status >= 400 && status < 500) {
        const current = await api.get(id);
        const target = TRANSITION_TARGET[request.transition];
        if (target !== undefined && current.state === target) {
          return {summary: `Inspection already ${request.transition}`};
        }
      }
      throw error;
    }
  };
